import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CreditCard,
  Building2,
  Globe,
  Instagram,
  ShieldCheck,
  AlertCircle,
  HelpCircle,
  CheckCircle2,
  Save,
  Key,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export default function OrganizerSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [org, setOrg] = useState(null);

  // Form fields
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [description, setDescription] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [instagramHandle, setInstagramHandle] = useState("");

  // Custom Razorpay
  const [customRazorpayEnabled, setCustomRazorpayEnabled] = useState(false);
  const [razorpayKeyId, setRazorpayKeyId] = useState("");
  const [razorpayKeySecret, setRazorpayKeySecret] = useState("");

  useEffect(() => {
    api
      .get("/admin/organizer/me")
      .then((r) => {
        const d = r.data;
        setOrg(d);
        setName(d.name || "");
        setTagline(d.tagline || "");
        setDescription(d.description || "");
        setLogoUrl(d.logo_url || "");
        setContactEmail(d.contact_email || "");
        setContactPhone(d.contact_phone || "");
        setWebsiteUrl(d.website_url || "");
        setInstagramHandle(d.instagram_handle || "");
        setCustomRazorpayEnabled(!!d.custom_razorpay_enabled);
        setRazorpayKeyId(d.razorpay_key_id || "");
        setRazorpayKeySecret(d.razorpay_key_secret || "");
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || "Could not load organizer profile");
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name,
        tagline,
        description,
        logo_url: logoUrl,
        contact_email: contactEmail,
        contact_phone: contactPhone,
        website_url: websiteUrl,
        instagram_handle: instagramHandle,
        custom_razorpay_enabled: customRazorpayEnabled,
        razorpay_key_id: razorpayKeyId || null,
        razorpay_key_secret: razorpayKeySecret || null,
      };

      const res = await api.put("/admin/organizer/me", payload);
      setOrg(res.data);
      toast.success("Organizer settings updated successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-10 text-center text-stone-500">Loading organizer settings…</div>;
  }

  const landingPageUrl = `https://register.cgcollective.in/events/${org?.slug || "offclub"}`;

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      <div>
        <div className="eyebrow">Collaboration Portal</div>
        <h1 className="font-display text-3xl font-extrabold mt-1">
          Organizer Profile & Gateway Settings
        </h1>
        <p className="text-stone-500 text-sm mt-1">
          Manage your public brand landing page, contact info, and custom Razorpay payment gateway credentials for direct ticket revenue settlements.
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-8">
        {/* Profile Card */}
        <div className="cgc-card p-6 space-y-5">
          <h2 className="text-lg font-bold font-display border-b border-border pb-3 flex items-center gap-2">
            <Building2 size={18} className="text-orange-600" /> Public Branding & Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Organizer Display Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required />
            </div>

            <div className="space-y-1.5">
              <Label>Short Tagline / Catchphrase</Label>
              <Input
                placeholder="e.g. Alternative Culture & Soundscapes"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>About Organizer / Bio</Label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full text-sm p-3 rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Describe your collective, music style, and event formats…"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Logo Image URL</Label>
              <Input
                placeholder="https://…/logo.png"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Instagram Handle</Label>
              <div className="relative">
                <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={15} />
                <Input
                  className="pl-9"
                  placeholder="e.g. offclub.vizag"
                  value={instagramHandle}
                  onChange={(e) => setInstagramHandle(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Official Website URL</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={15} />
                <Input
                  className="pl-9"
                  placeholder="https://offclub.in"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Landing Page Preview Link */}
          <div className="p-3 bg-stone-50 dark:bg-stone-900 rounded-lg flex items-center justify-between text-xs">
            <span className="text-stone-500">Your Public Hub URL:</span>
            <a
              href={landingPageUrl}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-orange-600 hover:underline font-semibold"
            >
              {landingPageUrl} ↗
            </a>
          </div>
        </div>

        {/* Custom Razorpay Payment Gateway Card */}
        <div className="cgc-card p-6 space-y-5">
          <div className="flex items-center justify-between border-b border-border pb-3">
            <div className="flex items-center gap-2">
              <CreditCard size={18} className="text-orange-600" />
              <h2 className="text-lg font-bold font-display">Razorpay Payment Gateway (Direct Bank Payout)</h2>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="enableRzp"
                checked={customRazorpayEnabled}
                onChange={(e) => setCustomRazorpayEnabled(e.target.checked)}
                className="rounded border-stone-300 text-orange-600 focus:ring-orange-500 w-4 h-4"
              />
              <label htmlFor="enableRzp" className="text-xs font-semibold text-stone-700 dark:text-stone-300 cursor-pointer select-none">
                Enable Custom Gateway
              </label>
            </div>
          </div>

          {/* Razorpay Bypass & Approval Guidance */}
          <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs text-amber-900 dark:text-amber-200 space-y-2">
            <div className="font-bold flex items-center gap-1.5">
              <HelpCircle size={15} className="text-amber-600 shrink-0" />
              How to Register & Bypass Website Verification with Razorpay:
            </div>
            <p className="leading-relaxed">
              When applying for your independent Razorpay merchant account, Razorpay requires a verified business website.
              Submit your dedicated co-branded landing page URL:
            </p>
            <div className="p-2 bg-white dark:bg-stone-900 rounded border border-amber-300 dark:border-amber-700 font-mono text-xs select-all text-stone-800 dark:text-stone-200">
              {landingPageUrl}
            </div>
            <p className="leading-relaxed">
              Razorpay checks this link to verify that you are hosting legitimate events with clear refund terms, contact info, and pricing. Once approved, paste your live API keys below. All attendee payments will go straight to your bank account!
            </p>
          </div>

          {customRazorpayEnabled && (
            <div className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label>Razorpay Key ID (Live / Test)</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={15} />
                  <Input
                    className="pl-9 font-mono text-xs"
                    placeholder="rzp_live_xxxxxxxxxxxxxxxx"
                    value={razorpayKeyId}
                    onChange={(e) => setRazorpayKeyId(e.target.value)}
                    required={customRazorpayEnabled}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Razorpay Key Secret</Label>
                <Input
                  type="password"
                  className="font-mono text-xs"
                  placeholder={org?.razorpay_key_secret_masked || "Enter secret key"}
                  value={razorpayKeySecret}
                  onChange={(e) => setRazorpayKeySecret(e.target.value)}
                  required={customRazorpayEnabled && !org?.razorpay_key_secret_masked}
                />
              </div>

              <div className="text-[11px] text-stone-500">
                If left disabled, bookings will use Common Ground Collective's primary verified payment gateway.
              </div>
            </div>
          )}
        </div>

        <Button type="submit" disabled={saving} className="btn-brand px-6 py-2.5 text-sm flex items-center gap-2">
          <Save size={15} /> {saving ? "Saving…" : "Save Organizer Settings"}
        </Button>
      </form>
    </div>
  );
}
